import React, { useState, useContext } from "react";
import qs from "query-string";
import IconButton from "@material-ui/core/IconButton";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import InputAdornment from "@material-ui/core/InputAdornment";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import moment from "moment";
import { toast } from 'react-toastify'; 
import toastError from '../../errors/toastError';
import 'react-toastify/dist/ReactToastify.css';
import ColorModeContext from "../../layout/themeContext";
import UserLanguageSelector from "../../components/UserLanguageSelector";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import CircularProgress from "@material-ui/core/CircularProgress";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";


const logo = `${process.env.REACT_APP_BACKEND_URL}/public/logotipos/login.png`;
const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    background: theme.palette.background.default,
  },
  
  // Seção Hero (Esquerda - Visual/Marketing)
  heroSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6),
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #171717 0%, #2d2d2d 100%)"
      : "linear-gradient(135deg, #3ecc96 0%, #2de195 100%)",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  
  bgPattern: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)",
    backgroundSize: "32px 32px",
    zIndex: 0,
  },
  
  heroContent: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    color: "#fff",
    animation: "$fadeInLeft 0.8s ease-out",
  },
  
  heroLogo: {
    width: "80%",
    maxWidth: 400,
    height: "auto",
    marginBottom: theme.spacing(4),
    filter: "brightness(0) invert(1)",
  },
  
  heroTitle: {
    fontWeight: 700,
    fontSize: "2.5rem",
    marginBottom: theme.spacing(2),
    lineHeight: 1.2,
  },
  
  heroSubtitle: {
    fontSize: "1.25rem",
    opacity: 0.9,
    fontWeight: 400,
    maxWidth: 500,
    margin: "0 auto",
  },

  // Seção de Formulário (Direita)
  formSection: {
    width: "100%",
    maxWidth: 550,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
  },
  
  formContainer: {
    width: "100%",
    maxWidth: 400,
    animation: "$fadeInRight 0.8s ease-out",
  },
  
  mobileLogo: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "block",
      width: 220,
      margin: "0 auto 32px",
    },
  },
  
  welcomeText: {
    color: theme.palette.text.secondary,
    fontSize: "1rem",
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  
  formTitle: {
    fontWeight: 700,
    fontSize: "1.8rem",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(4),
  },
  
  form: {
    width: "100%",
  },
  
  inputField: {
    marginBottom: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.01)",
      transition: "all 0.2s ease",
      "& fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderWidth: 2,
        borderColor: theme.palette.primary.main,
      }
    }
  },
  
  submitButton: {
    margin: theme.spacing(2, 0, 3),
    padding: theme.spacing(1.8),
    borderRadius: 12,
    fontWeight: 700,
    fontSize: "1rem",
    textTransform: "none",
    boxShadow: "0 4px 14px 0 rgba(62, 204, 150, 0.39)",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(62, 204, 150, 0.45)",
    },
  },
  
  linkText: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "0.95rem",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    }
  },
  
  footerText: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
    marginTop: theme.spacing(2),
  },

  topActions: {
    position: "fixed",
    top: theme.spacing(2),
    right: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    zIndex: 10,
  },
  
  actionButton: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.8)",
    backdropFilter: "blur(8px)",
    borderRadius: 12,
    boxShadow: theme.palette.type === "dark" ? "none" : "0 2px 8px rgba(0,0,0,0.1)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}`,
    color: theme.palette.type === "dark" ? "#fff" : theme.palette.text.primary,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#fff",
      transform: "translateY(-1px)",
      boxShadow: theme.palette.type === "dark" ? "none" : "0 4px 12px rgba(0,0,0,0.15)",
    },
    [theme.breakpoints.down("sm")]: {
       color: "#fff",
       backgroundColor: "rgba(255,255,255,0.2)",
       border: "1px solid rgba(255,255,255,0.3)",
    }
  },

  // Keyframes
  "@keyframes fadeInLeft": {
    "0%": { opacity: 0, transform: "translateX(-30px)" },
    "100%": { opacity: 1, transform: "translateX(0)" }
  },
  "@keyframes fadeInRight": {
    "0%": { opacity: 0, transform: "translateX(30px)" },
    "100%": { opacity: 1, transform: "translateX(0)" }
  }
}));

const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

const ForgetPassword = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const history = useHistory();
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [showResetPasswordButton, setShowResetPasswordButton] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const toggleAdditionalFields = () => {
    setShowAdditionalFields(!showAdditionalFields);
    if (showAdditionalFields) {
      setShowResetPasswordButton(false);
    } else {
      setShowResetPasswordButton(true);
    }
  };

  const params = qs.parse(window.location.search);
  const initialState = { email: "" };
  const [user] = useState(initialState);
  const dueDate = moment().add(3, "day").format();

const handleSendEmail = async (values) => {
  const email = values.email;
  try {
    const response = await api.post(
      `${process.env.REACT_APP_BACKEND_URL}/forgetpassword/${email}`
    );
    console.log("API Response:", response.data);

    if (response.data.status === 404) {
      toast.error(i18n.t("forgetPassword.messages.emailNotFound"));
    } else {
      toast.success(i18n.t("forgetPassword.messages.emailSentSuccess"));
    }
  } catch (err) {
    console.log("API Error:", err);
    toastError(err);
  }
};

  const handleResetPassword = async (values) => {
    const email = values.email;
    const token = values.token;
    const newPassword = values.newPassword;
    const confirmPassword = values.confirmPassword;

    if (newPassword === confirmPassword) {
      try {
        await api.post(
          `${process.env.REACT_APP_BACKEND_URL}/resetpasswords/${email}/${token}/${newPassword}`
        );
        setError(""); // Limpe o erro se não houver erro
        toast.success(i18n.t("forgetPassword.messages.passwordResetSuccess"));
        history.push("/login");
      } catch (err) {
        console.log(err);
      }
    }
  };

  const isResetPasswordButtonClicked = showResetPasswordButton;
  const UserSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Required"),
    newPassword: isResetPasswordButtonClicked
      ? Yup.string()
          .required("Campo obrigatório")
          .matches(
            passwordRegex,
            i18n.t("forgetPassword.messages.passwordRequirements")
          )
      : Yup.string(), // Sem validação se não for redefinição de senha
    confirmPassword: Yup.string().when("newPassword", {
      is: (newPassword) => isResetPasswordButtonClicked && newPassword,
      then: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "As senhas não correspondem")
        .required("Campo obrigatório"),
      otherwise: Yup.string(), // Sem validação se não for redefinição de senha
    }),
  });

  const logoWithRandom = `${logo}?r=${Math.random()}`;

  return (
    <div className={classes.root}>
      {/* Botões de Tema e Idioma */}
      <div className={classes.topActions}>
        <IconButton 
          className={classes.actionButton}
          onClick={colorMode.toggleColorMode}
          edge="end"
        >
          {theme.palette.type === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
        
        <div className={classes.actionButton} style={{ borderRadius: 12, overflow: 'hidden' }}>
          <UserLanguageSelector />
        </div>
      </div>

      {/* Lado Esquerdo - Hero Section */}
      <div className={classes.heroSection}>
        <div className={classes.bgPattern} />
        <div className={classes.heroContent}>
          <img
            src={logoWithRandom}
            alt="Logo Hero"
            className={classes.heroLogo}
          />
          <Typography className={classes.heroTitle}>
            {i18n.t("forgetPassword.hero.title")}
          </Typography>
          <Typography className={classes.heroSubtitle}>
            {i18n.t("forgetPassword.hero.subtitle")}
          </Typography>
        </div>
      </div>

      {/* Lado Direito - Form Section */}
      <div className={classes.formSection}>
        <div className={classes.formContainer}>
          <img
            src={logoWithRandom}
            alt="Logo Mobile"
            className={classes.mobileLogo}
          />
          
          <Typography className={classes.welcomeText}>
            {i18n.t("forgetPassword.hero.title")}
          </Typography>
          <Typography className={classes.formTitle}>
            {i18n.t("forgetPassword.formTitle")}
          </Typography>

          <Formik
            initialValues={{
              email: "",
              token: "",
              newPassword: "",
              confirmPassword: "",
            }}
            enableReinitialize={true}
            validationSchema={UserSchema}
            onSubmit={(values, actions) => {
              setTimeout(() => {
                if (showResetPasswordButton) {
                  handleResetPassword(values);
                } else {
                  handleSendEmail(values);
                }
                actions.setSubmitting(false);
                // toggleAdditionalFields(); // Keep fields visible if reset fails or for better UX
              }, 400);
            }}
          >
            {({ touched, errors, isSubmitting }) => (
              <Form className={classes.form}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      id="email"
                      label={i18n.t("signup.form.email")}
                      name="email"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      autoComplete="email"
                      required
                      className={classes.inputField}
                    />
                  </Grid>
                  {showAdditionalFields && (
                    <>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          variant="outlined"
                          fullWidth
                          id="token"
                          label={i18n.t("forgetPassword.form.verificationCode")}
                          name="token"
                          error={touched.token && Boolean(errors.token)}
                          helperText={touched.token && errors.token}
                          autoComplete="off"
                          required
                          className={classes.inputField}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          variant="outlined"
                          fullWidth
                          type={showPassword ? "text" : "password"}
                          id="newPassword"
                          label={i18n.t("forgetPassword.form.newPassword")}
                          name="newPassword"
                          error={
                            touched.newPassword &&
                            Boolean(errors.newPassword)
                          }
                          helperText={
                            touched.newPassword && errors.newPassword
                          }
                          autoComplete="off"
                          required
                          className={classes.inputField}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={togglePasswordVisibility}
                                  edge="end"
                                >
                                  {showPassword ? (
                                    <VisibilityIcon />
                                  ) : (
                                    <VisibilityOffIcon />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          variant="outlined"
                          fullWidth
                          type={showConfirmPassword ? "text" : "password"}
                          id="confirmPassword"
                          label={i18n.t("forgetPassword.form.confirmPassword")}
                          name="confirmPassword"
                          error={
                            touched.confirmPassword &&
                            Boolean(errors.confirmPassword)
                          }
                          helperText={
                            touched.confirmPassword &&
                            errors.confirmPassword
                          }
                          autoComplete="off"
                          required
                          className={classes.inputField}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={toggleConfirmPasswordVisibility}
                                  edge="end"
                                >
                                  {showConfirmPassword ? (
                                    <VisibilityIcon />
                                  ) : (
                                    <VisibilityOffIcon />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : showResetPasswordButton ? (
                    i18n.t("forgetPassword.buttons.resetPassword")
                  ) : (
                    i18n.t("forgetPassword.buttons.sendEmail")
                  )}
                </Button>

                <Grid container direction="column" alignItems="center">
                  <Grid item>
                    <Link
                      component={RouterLink}
                      to="/login"
                      className={classes.linkText}
                    >
                      {i18n.t("forgetPassword.links.backToLogin")}
                    </Link>
                  </Grid>
                  
                  <Grid item>
                    <Typography className={classes.footerText}>
                      {i18n.t("forgetPassword.links.noAccount")}{" "}
                      <Link
                        component={RouterLink}
                        to="/signup"
                        className={classes.linkText}
                      >
                        {i18n.t("signup.title")}
                      </Link>
                    </Typography>
                  </Grid>
                </Grid>
                
                {error && (
                  <Box mt={2}>
                    <Typography variant="body2" color="error" align="center">
                      {error}
                    </Typography>
                  </Box>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;
